import { motion } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";

const Hero = () => {
  const { t } = useLanguage();
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-center overflow-hidden bg-white pt-24"
    >
      {/* Atmospheric glow background */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 -left-32 w-[36rem] h-[36rem] rounded-full bg-[#1B2A4A] opacity-[0.06] blur-3xl" />
        <div className="absolute top-1/3 -right-40 w-[40rem] h-[40rem] rounded-full bg-[#3B82F6] opacity-[0.08] blur-3xl" />
        <div className="absolute bottom-0 left-1/3 w-[28rem] h-[28rem] rounded-full bg-[#1B2A4A] opacity-[0.04] blur-3xl" />
      </div>

      {/* Fine grid texture */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.025]"
        style={{
          backgroundImage:
            "linear-gradient(to right, #1B2A4A 1px, transparent 1px), linear-gradient(to bottom, #1B2A4A 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full">
        <div className="max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.25, 0.1, 0.25, 1] }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#1B2A4A]/10 bg-white/70 backdrop-blur-sm mb-8"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] animate-pulse" />
            <span className="text-xs md:text-sm font-medium text-[#475569] tracking-wide">
              {t.hero.badge}
            </span>
          </motion.div>

          <motion.h1
            key={t.hero.headlineStart}
            data-testid="hero-headline"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
            className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-[#1B2A4A] leading-[1.05]"
          >
            {t.hero.headlineStart}{" "}
            <span className="relative inline-block">
              <span className="italic font-medium text-[#3B82F6]">
                {t.hero.headlineHighlight}
              </span>
            </span>{" "}
            {t.hero.headlineEnd}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
            className="mt-8 max-w-2xl text-lg md:text-xl text-[#475569] leading-relaxed font-light"
          >
            {t.hero.description}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: [0.25, 0.1, 0.25, 1] }}
            className="mt-12 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta"
              onClick={() => scrollTo("kapcsolat")}
              className="group inline-flex items-center gap-3 rounded-full bg-[#1B2A4A] px-8 py-4 text-base font-medium text-white transition-all duration-300 hover:bg-[#2A4070] active:scale-95 shadow-[0_12px_32px_-12px_rgba(27,42,74,0.5)]"
            >
              <span>{t.hero.ctaPrimary}</span>
              <span className="inline-block transition-transform duration-300 group-hover:translate-x-1.5">
                →
              </span>
            </button>

            <button
              data-testid="hero-secondary-cta"
              onClick={() => scrollTo("szolgaltatasok")}
              className="inline-flex items-center gap-2 rounded-full border border-[#1B2A4A]/15 bg-white/60 backdrop-blur-sm px-8 py-4 text-base font-medium text-[#1B2A4A] transition-all duration-300 hover:bg-white hover:border-[#1B2A4A]/30 active:scale-95"
            >
              {t.hero.ctaSecondary}
            </button>
          </motion.div>

          {/* Trust indicators */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="mt-16 flex flex-wrap items-center gap-8 md:gap-12"
          >
            {t.hero.stats.map((s, i) => (
              <div key={i} className="flex flex-col">
                <span className="font-serif text-3xl md:text-4xl font-bold text-[#1B2A4A]">
                  {s.v}
                </span>
                <span className="text-xs md:text-sm text-[#475569] mt-1 tracking-wide uppercase">
                  {s.l}
                </span>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2"
      >
        <span className="text-xs uppercase tracking-[0.2em] text-[#475569]">
          {t.hero.scrollHint}
        </span>
        <div className="w-px h-12 bg-gradient-to-b from-[#1B2A4A]/40 to-transparent" />
      </motion.div>
    </section>
  );
};

export default Hero;
